import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Send, Calendar, Loader2, CalendarDays } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/shared/DatePicker'
import { leaveService } from '@/services/leaveService'

const leaveSchema = z.object({
  leave_type: z.string().min(1, 'Please select leave type'),
  start_date: z.date({ required_error: 'Please select start date' }),
  end_date: z.date({ required_error: 'Please select end date' }),
  reason: z.string().optional(),
}).refine((data) => {
  if (data.start_date && data.end_date) {
    return data.end_date >= data.start_date
  }
  return true
}, {
  message: "End date must be after start date",
  path: ["end_date"],
})

const LeaveApplicationForm = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [calculatedDays, setCalculatedDays] = useState(0)
  const [existingLeaves, setExistingLeaves] = useState([])
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  const form = useForm({
    resolver: zodResolver(leaveSchema),
    defaultValues: {
      leave_type: '',
      start_date: undefined,
      end_date: undefined,
      reason: '',
    },
  })

  // Fetch existing leaves to check for overlaps
  useEffect(() => {
    const fetchExistingLeaves = async () => {
      try {
        const response = await leaveService.getAllLeaves()
        if (response.success) {
          setExistingLeaves(response.data.leaves || [])
        }
      } catch (error) {
        console.error('Failed to fetch existing leaves:', error)
      }
    }
    fetchExistingLeaves()
  }, [])

  const leaveTypes = [
    { value: 'sick', label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
    { value: 'vacation', label: 'Vacation Leave', color: 'bg-blue-100 text-blue-700' },
    { value: 'personal', label: 'Personal Leave', color: 'bg-purple-100 text-purple-700' },
    { value: 'maternity', label: 'Maternity Leave', color: 'bg-pink-100 text-pink-700' },
    { value: 'paternity', label: 'Paternity Leave', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'emergency', label: 'Emergency Leave', color: 'bg-orange-100 text-orange-700' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
  ]

  // Check for overlapping leave requests
  const checkForOverlap = (startDate, endDate) => {
    if (!startDate || !endDate) return null
    
    const start = dayjs(startDate).format('YYYY-MM-DD')
    const end = dayjs(endDate).format('YYYY-MM-DD')
    
    const overlappingLeave = existingLeaves.find(leave => {
      if (!['pending', 'approved'].includes(leave.status)) return false
      
      const leaveStart = leave.start_date
      const leaveEnd = leave.end_date
      
      return (start <= leaveEnd && end >= leaveStart)
    })
    
    return overlappingLeave
  }

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      // Check for overlapping leave requests before submission
      const overlappingLeave = checkForOverlap(values.start_date, values.end_date)
      if (overlappingLeave) {
        toast.error(`Cannot submit leave request: You already have a ${overlappingLeave.status} leave request from ${overlappingLeave.start_date} to ${overlappingLeave.end_date}`)
        setLoading(false)
        return
      }

      const formattedValues = {
        ...values,
        start_date: dayjs(values.start_date).format('YYYY-MM-DD'),
        end_date: dayjs(values.end_date).format('YYYY-MM-DD')
      }

      const response = await leaveService.createLeave(formattedValues)
      
      if (response.success) {
        toast.success('Leave request submitted successfully!')
        form.reset()
        setHasUnsavedChanges(false)
        setCalculatedDays(0)
        
        // Refresh existing leaves list
        const updatedResponse = await leaveService.getAllLeaves()
        if (updatedResponse.success) {
          setExistingLeaves(updatedResponse.data.leaves || [])
        }
        
        onSuccess?.(response.data.leave)
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit leave request')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true)
    } else {
      onCancel()
    }
  }

  // Watch for date changes to calculate days
  const watchStartDate = form.watch('start_date')
  const watchEndDate = form.watch('end_date')
  const watchLeaveType = form.watch('leave_type')

  const selectedLeaveType = leaveTypes.find(t => t.value === watchLeaveType)

  useEffect(() => {
    if (watchStartDate && watchEndDate) {
      const diffTime = dayjs(watchEndDate).diff(dayjs(watchStartDate), 'day') + 1
      setCalculatedDays(diffTime > 0 ? diffTime : 0)
      
      // Check for overlapping leave requests
      const overlappingLeave = checkForOverlap(watchStartDate, watchEndDate)
      if (overlappingLeave) {
        toast.warning(`You already have a ${overlappingLeave.status} leave request from ${overlappingLeave.start_date} to ${overlappingLeave.end_date}`)
      }
    } else {
      setCalculatedDays(0)
    }
  }, [watchStartDate, watchEndDate])

  return (
    <div className="bg-background">
      {/* Header */}
      <div className="px-6 py-5 border-b bg-muted/30">
        <h3 className="text-lg font-semibold text-foreground">Apply for Leave</h3>
        <p className="text-sm text-muted-foreground mt-1">Submit your leave request for review and approval</p>
      </div>

      {/* Form */}
      <div className="p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onChange={() => setHasUnsavedChanges(true)} className="space-y-5">
            <FormField
              control={form.control}
              name="leave_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Leave Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {leaveTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <Badge variant="secondary" className={type.color}>
                            {type.label}
                          </Badge>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select date"
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select date"
                        disabled={(date) => {
                          const startDate = form.getValues('start_date')
                          return date < new Date(new Date().setHours(0, 0, 0, 0)) || 
                                 (startDate && date < startDate)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Duration Display */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Duration</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-primary">
                    {calculatedDays}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    {calculatedDays === 1 ? 'day' : 'days'}
                  </span>
                </div>
              </div>
              {watchStartDate && watchEndDate && (
                <p className="text-xs text-muted-foreground mt-2">
                  {dayjs(watchStartDate).format('MMM D, YYYY')} - {dayjs(watchEndDate).format('MMM D, YYYY')}
                </p>
              )}
            </div>

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Please provide a reason for your leave request..."
                      className="min-h-[100px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Optional but recommended for faster approval</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </div>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default LeaveApplicationForm
